<template>
  <form v-if="value" @submit.prevent="onSubmit">
    <form-sticky-header
      :title="title"
      :links="{
        'form-general': 'Général',
        'form-images': 'Images',
      }"
    >
      <slot name="actions"></slot>
    </form-sticky-header>

    <form-fieldset title="Général" id="form-general">
      <template slot="description">
        <p class="text-muted">
          Les collections contiennent des
          <router-link :to="{ name: 'products' }">produits</router-link>, qui
          déterminent leur affichage dans les différentes sous-sections
          (Vêtements, Linges, Tapis, Accessoires).<br />
          <br />
          N'oubliez pas de <strong>publier votre collection</strong> si celle-ci
          n'apparaît pas dans la liste.
        </p>
        <hr />
        <form-group>
          <form-switch v-model="model.published"
            >Publier cette collection</form-switch
          >
        </form-group>
      </template>

      <row>
        <column sm="6">
          <form-group label="Nom de la collection" required>
            <form-input
              type="text"
              v-model.trim="model.name"
              placeholder="Ex : Millésime"
              required
            />
            <span slot="help"
              >N'incluez <strong>pas</strong> le mot
              &laquo;&nbsp;Collection&nbsp;&raquo;&nbsp;: il est rajouté
              automatiquement si nécessaire.</span
            >
          </form-group>
        </column>
        <column sm="6">
          <form-group label="Alias de la collection">
            <form-input
              type="text"
              pattern="[A-Za-z0-9\-_]+"
              title="Seulement caractères autorisés dans les URLs"
              v-model.trim="model.slug"
              placeholder="(généré automatiquement)"
            />
            <span slot="help"
              >Est utilisé dans les URL (automatiquement généré si laissé
              vide).</span
            >
          </form-group>
        </column>
      </row>

      <row>
        <column sm="6">
          <form-group label="Slogan / Accroche">
            <form-input
              type="text"
              v-model.trim="model.baseline"
              placeholder="Ex : Quand élégance rime avec confort"
            />
            <span slot="help"
              >Le slogan est affiché sous le nom de la collection lors de la
              prévisualisation de cette dernière.</span
            >
          </form-group>
        </column>
        <column sm="6">
          <form-group label="Métier(s)">
            <form-multiselect
              v-model="model.categories"
              :options="categories"
              multiple
            />
            <span slot="help"
              >Rendez-vous sur la partie
              <router-link :to="{ name: 'categories' }">métiers</router-link>
              pour modifier cette liste.</span
            >
          </form-group>
        </column>
      </row>

      <row>
        <column sm="6">
          <form-group label="Ordre d'affichage">
            <form-order v-model.numeric="model.order" />
          </form-group>
        </column>
        <column sm="6">
          <form-group label="Mise en avant">
            <form-checkbox v-model="model.featured"
              >Afficher dans les dernières collections</form-checkbox
            >
          </form-group>
        </column>
      </row>
    </form-fieldset>

    <form-fieldset title="Image de prévisualisation" id="form-images">
      <template slot="description">
        <p class="text-muted">
          Cette image est utilisée comme fond d'accueil de la collection sur
          l'interface.<br />
          Choisissez une image de préférence JPEG de grande taille et de format
          adapté aux tablettes en paysage (ex : 2400&times;1800 px).
        </p>
      </template>

      <form-file-upload v-model="model.preview" image />
    </form-fieldset>

    <form-fieldset title="Image de liste">
      <template slot="description">
        <p class="text-muted">
          Cette image est utilisée dans la liste des collections affichées
          lorsqu'un métier est choisi.<br />
          Choisissez une image de préférence JPEG de grande taille et de format
          adapté aux tablettes en portrait (ex : 540&times;1140 px).
        </p>
      </template>

      <form-file-upload v-model="model.listView" image />
    </form-fieldset>

    <form-fieldset title="Image sur les nouveautés (accueil)">
      <template slot="description">
        <p class="text-muted">
          Cette image est utilisée dans la liste des nouveautés affichées sur la
          page d'accueil.<br />
          Choisissez une image de préférence JPEG de grande taille et de format
          adapté aux tablettes en portrait (ex : 960&times;720 px).
        </p>
      </template>

      <form-file-upload v-model="model.featuredView" image />
    </form-fieldset>

    <form-fieldset title="Image de détails">
      <template slot="description">
        <p class="text-muted">
          Cette image est utilisée pour la vue EN SAVOIR +.<br />
          Choisissez une image de préférence JPEG de grande taille et de format
          adapté aux tablettes en paysage (ex : 2400&times;1800 px).
        </p>
      </template>

      <form-file-upload v-model="model.details" image />
    </form-fieldset>

    <form-fieldset title="Image pour PDF">
      <template slot="description">
        <p class="text-muted">
          Cette image est utilisée pour les PDF générés.<br />
          Choisissez une image de préférence JPEG ou PNG de grande taille, haute
          résolution, et de format 21 x 29.7 cm — A4 portrait (ex :
          2100&times;2970 px).
        </p>
      </template>

      <form-file-upload v-model="model.orderDetails" image />
    </form-fieldset>
  </form>
</template>

<script>
import _omit from "lodash/omit";
import _map from "lodash/map";

import CategoryListQuery from "../categories/graphql/CategoryListQuery.gql";

export default {
  components: {
    FormStickyHeader: () => import("../../components/FormStickyHeader.vue"),
    FormFileUpload: () => import("../../components/FormFileUpload.vue"),
    FormOrder: () => import("../../components/FormOrder.vue"),
    FormSwitch: () =>
      import("package-common-vuejs/src/components/form/FormSwitch"),
    FormMultiselect: () =>
      import("package-common-vuejs/src/components/form/FormMultiselect"),
  },
  props: {
    title: String,
    value: Object,
  },
  apollo: {
    categories: {
      query: CategoryListQuery,
      fetchPolicy: "network-only",
      variables() {
        return {
          pagination: {
            limit: 999,
          },
        };
      },
      update: (data) =>
        _map(data.results.edges, ({ node: { id, name } }) => ({
          value: id,
          label: name,
        })),
    },
  },
  data() {
    return {
      model: {},
      categories: [],
    };
  },
  computed: {},
  created() {
    this.updateModel(this.value);
  },
  watch: {
    value() {
      this.updateModel(this.value);
    },
  },
  methods: {
    updateModel(value) {
      this.model = {
        ...value,
        preview: value && value.preview ? value.preview.id : null,
        listView: value && value.listView ? value.listView.id : null,
        featuredView:
          value && value.featuredView ? value.featuredView.id : null,
        details: value && value.details ? value.details.id : null,
        orderDetails:
          value && value.orderDetails ? value.orderDetails.id : null,
        categories:
          value && value.categories ? _map(value.categories, "id") : [],
      };
    },
    onSubmit() {
      this.$emit(
        "submit",
        _omit(
          {
            ...this.model,
          },
          ["id", "__typename", "createdAt", "updatedAt", "products"]
        )
      );
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/pages/collections/CollectionForm.vue